import { captureException, captureMessage } from "@sentry/browser";
import { formatMoneyValue } from "../../utils/moneyUtils";

/**
 * Gets a data attribute value from a specified element with error tracking.
 * @param {string} tagName - The name of the data attribute (without 'data-' prefix).
 * @param {string} elementId - The ID of the element to get the data from.
 * @returns {string|null} The data attribute value or null if not found.
 */
export function getDataTagValue(tagName, elementId = "top") {
  const headerElement = document.getElementById(elementId);
  if (headerElement === null) {
    captureMessage(`Element '#${elementId}' is missing`, {
      level: "error",
      tags: {
        function_context: "getDataTagValue",
        failure_reason: "element_not_found",
        target_element_id: elementId,
      },
      extra: {
        context_message: `Failed to find element with ID '${elementId}'. Function 'getDataTagValue' will return null.`,
        requested_tag_name: tagName,
      },
    });

    return null;
  }

  const tagIsUndefined = typeof headerElement.dataset[tagName] === "undefined";
  if (tagIsUndefined) {
    captureMessage(
      `Data attribute 'data-${tagName}' missing on element '#${elementId}'.`,
      {
        level: "error",
        tags: {
          function_context: "getDataTagValue",
          failure_reason: "data_attribute_missing",
          target_element_id: elementId,
        },
        extra: {
          context_message: `Failed to find data attribute 'data-${tagName}' on element with ID '${elementId}'. Function 'getDataTagValue' will return null.`,
          requested_tag_name: tagName,
        },
      },
    );

    return null;
  }
  const tagValue = headerElement.dataset[tagName].trim();

  if (tagValue !== "") {
    return tagValue;
  }
  return null;
}

/**
 * Gets an element by ID with critical error tracking.
 * @param {string} elementId - The ID of the element to retrieve (defaults to 'top').
 * @returns {HTMLElement|null} The element or null if not found.
 */
export function getElementByIdSafe(elementId = "top") {
  const element = document.getElementById(elementId);

  if (!element) {
    // This is a critical error - the element should exist
    const error = new Error(
      `Critical: Element with ID '${elementId}' not found`,
    );

    captureException(error, {
      level: "error",
      tags: {
        function_context: "getElementByIdSafe",
        element_id: elementId,
        critical_error: true,
      },
      extra: {
        context_message: `Failed to find critical element with ID '${elementId}'. This is likely a template or DOM issue.`,
      },
    });

    return null;
  }

  return element;
}

/**
 * Gets the user data element from either main site or checkout pages.
 * Checks for 'top' (main site) first, then 'checkout-user-data' (checkout pages).
 * @returns {HTMLElement|null} The user data element or null if neither exists.
 */
export function getUserDataElement() {
  // Try main site header first
  const mainElement = document.getElementById("top");
  if (mainElement) {
    return mainElement;
  }

  // Fallback to checkout user data element
  const checkoutElement = document.getElementById("checkout-user-data");
  if (checkoutElement) {
    return checkoutElement;
  }

  // Neither element found - log once for both attempts
  captureMessage("No user data element found", {
    level: "error",
    tags: {
      function_context: "getUserDataElement",
      critical_error: true,
    },
    extra: {
      context_message:
        "Failed to find either 'top' or 'checkout-user-data' elements. User identification will not work.",
    },
  });

  return null;
}

/**
 * Gets the currency value from the data tag.
 * @returns {string} The currency name value.
 */
export function getCurrencyValue() {
  return getDataTagValue("currencyName");
}

/**
 * Gets a boolean data attribute value from a specified element with error tracking.
 * @param {string} tagName - The name of the data attribute (without 'data-' prefix).
 * @param {string} elementId - The ID of the element to get the data from.
 * @returns {boolean|null} The boolean value (true for "1", false for "0") or null if not found.
 */
export function getDataTagBoolean(tagName, elementId = "top") {
  const value = getDataTagValue(tagName, elementId);

  if (value === null) {
    return null;
  }

  // Convert "1"/"0" to boolean following codebase convention
  return value === "1";
}

/**
 * Transforms a data attribute value based on its type supporting boolean, number, price, and string conversions.
 * @param {string|null} value - The raw value from the data attribute.
 * @param {string} type - The type to transform to ('boolean', 'number', 'price', or 'string').
 * @param {string} attributeName - The name of the attribute for error logging.
 * @returns {*} The transformed value or null if empty/invalid.
 * @example
 * transformValue("1", "boolean", "isActive") // returns true
 * transformValue("0", "boolean", "isActive") // returns false
 * transformValue("25.99", "price", "productPrice") // returns 25.99 (formatted)
 * transformValue("10", "number", "quantity") // returns 10
 * transformValue("hello", "string", "name") // returns "hello"
 */
export function transformDataValue(value, type = "string", attributeName = "") {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  try {
    switch (type) {
      case "boolean":
        // Convert "1"/"0" to boolean following codebase convention
        return value === "1";

      case "number": {
        const num = parseInt(value, 10);
        return isNaN(num) ? null : num;
      }

      case "price":
        return formatMoneyValue(value);

      case "string":
      default:
        // String type - return as is
        return value;
    }
  } catch (error) {
    // Log transformation errors
    captureException(error, {
      level: "warning",
      tags: {
        function_context: "transformDataValue",
        value_type: type,
        attribute_name: attributeName,
      },
      extra: {
        raw_value: value,
        error_message: error.message,
      },
    });

    return null;
  }
}

/**
 * Gets the value from an element using jQuery selector by ID with error tracking.
 * @param {string} elementId - The ID of the element (without # prefix).
 * @param {string} valueMethod - The jQuery method to get the value (e.g., 'val', 'text', 'html').
 * @returns {string|null} The element's value or null if not found.
 */
export function getElementValueById(elementId, valueMethod = "val") {
  // Validate valueMethod
  const validMethods = ["val", "text", "html"];
  if (!validMethods.includes(valueMethod)) {
    const error = new Error(`Invalid value method: ${valueMethod}`);
    captureException(error, {
      level: "error",
      tags: {
        function_context: "getElementValueById",
        element_id: elementId,
        invalid_method: valueMethod,
      },
      extra: {
        context_message: `Invalid jQuery method '${valueMethod}'. Valid methods are: ${validMethods.join(", ")}`,
      },
    });
    return null;
  }

  const $element = $(`#${elementId}`);

  if ($element.length === 0) {
    // Element not found - log error to Sentry
    captureMessage(`Element '#${elementId}' not found`, {
      level: "error",
      tags: {
        function_context: "getElementValueById",
        failure_reason: "element_not_found",
        target_element_id: elementId,
      },
      extra: {
        context_message: `Failed to find element with ID '${elementId}'. Function 'getElementValueById' will return null.`,
        requested_method: valueMethod,
      },
    });
    return null;
  }

  try {
    // Get the value using the specified method
    const value = $element[valueMethod]();

    // Handle different return types
    if (value === undefined || value === null) {
      return null;
    }

    // Convert to string and trim
    const stringValue = String(value).trim();
    return stringValue !== "" ? stringValue : null;
  } catch (error) {
    // Handle any errors during value extraction
    captureException(error, {
      level: "error",
      tags: {
        function_context: "getElementValueById",
        element_id: elementId,
        extraction_error: true,
      },
      extra: {
        context_message: `Error extracting value from element '${elementId}' using method '${valueMethod}'`,
        error_message: error.message,
      },
    });
    return null;
  }
}
